import { CardIcon, LoopButton, Modal, Shadow, Toast, Typography, Stepper } from '@loophealth/loop-ui-web-library';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ShruggingGraphic } from '../../../assets/img';
import { Close, InfoIcon } from '../../../assets/img/CustomIcons';
import {
    setModularSelectedAlaCarte,
    setModularUserBenefits,
    setPurchaseAttributes
} from '../../../redux/slices/ModularEnrolmentSlice';
import {
    selectedAlaCarteBenefits,
    selectModularPurchaseAttributes
} from '../../../redux/slices/ModularEnrolmentSlice/selectors';
import theme from '../../../theme';
import WithThemeProvider from '../../../theme/WithThemeProvider';
import { formatAmount } from '../../../utils/common';
import { StyledClose } from '../ModularFtuxModal/styles';
import {
    StyledBenefitPerLifeRemovalModal,
    StyledBenefitPerLifeRemovalModalBody,
    StyledBenefitPerLifeRemovalModalContent,
    StyledBenefitPerLifeRemovalModalFooter,
    StyledBenefitPerLifeSelectionModal,
    StyledBenefitPerLifeSelectionModalFooter,
    StyledBenefitPerLifeSelectionModalFooterError,
    StyledBenefitPerLifeSelectionModalFooterLeft,
    StyledBenefitPerLifeSelectionModalFooterRight,
    StyledBenefitPerLifeSelectionModalHeader,
    StyledBenefitPerLifeSelectionModalHeaderText,
    StyledBenefitPrimaryInfo,
    StylePerLifeUserLogoHeaders
} from './styles';
import { IBenefitPerLifeSelectionModal } from './types';
import {
    calculatePerLifeTotalCost,
    generatePerLifeMemberConfig,
    getBenefitPerLifeCurrentProceedStatus,
    getDefaultSelectedUserBenefits,
    getPerLifeUpdatedSelections,
    handlePerLifeFormUpdations,
    validatePerLifeFormSelection,
    getUpdatedAttributesForPerLife
} from './utils';
import PurchaseAttributePerLifeModal from './PurchaseAttributesPerLifeModal';
import { FlagsmithFlags, useFeatureFlag } from '../../../utils/flagsmith';
import { ISelectedAttributes } from '../../../redux/slices/ModularEnrolmentSlice/types';

const BenefitPerLifeSelectionModal: React.FC<IBenefitPerLifeSelectionModal> = ({
    isVisible,
    setIsVisible,
    userBenefits,
    family,
    plan,
    planIds,
    deletionModalVisible,
    setDeletionModalVisible,
    setSelectedItem
}) => {
    const purchaseAttributes = plan.purchaseAttributes || {};
    const dispatch = useDispatch();
    const toast = Toast.useToast();
    const [showError, setShowError] = useState(false);
    const selectedBenefits = useSelector(selectedAlaCarteBenefits);
    const [values, setValues] = useState(() => getDefaultSelectedUserBenefits(plan, userBenefits, family));
    const { members, uncoveredText } = useMemo(() => generatePerLifeMemberConfig(plan, family), [family, plan]);
    const isPurchaseAttributesEnabled = useFeatureFlag(FlagsmithFlags.PURCHASE_ATTRIBUTES);
    const hasPurchaseAttributes = isPurchaseAttributesEnabled
        ? Object.keys(plan.purchaseAttributes ?? {}).length > 0
        : false;
    const totalCost = useMemo(() => calculatePerLifeTotalCost(plan, values), [plan, values]);
    const isCostZero = totalCost === 0;
    const { hasSelectedBenefits, hasNotSelectedValues } = getBenefitPerLifeCurrentProceedStatus(values);
    const isProceedEnabled = hasSelectedBenefits && !hasNotSelectedValues;
    const [step, setStep] = useState(1);
    const selectedMembers = Object.keys(values).filter((id) => values[id].checked);
    const selectedMemberDetails = members.filter((member) => selectedMembers.includes(member.data.id));
    const selectedAttributes = useSelector(selectModularPurchaseAttributes) ?? {};
    const handleCloseModal = () => {
        setIsVisible(false);
        setStep(1);
    };
    const removeAllExtraPurchaseAttributes = (
        isEditMode: boolean,
        planIds: string[],
        updatedBenefits: string[],
        selectedMembers: string[],
        selectedAttributes: ISelectedAttributes
    ) => {
        if (!isEditMode) {
            return selectedAttributes;
        }
        const updatedAttributes = {};
        Object.entries(selectedAttributes).forEach(([fieldKey, fieldValue]) => {
            const shouldKeepField =
                !planIds.includes(fieldKey) || (planIds.includes(fieldKey) && updatedBenefits.includes(fieldKey));

            if (shouldKeepField && fieldValue) {
                updatedAttributes[fieldKey] = Object.fromEntries(
                    Object.entries(fieldValue).filter(([userId]) => selectedMembers.includes(userId))
                );
            }
        });

        return updatedAttributes;
    };
    const [formValues, setFormValues] = useState(() =>
        handlePerLifeFormUpdations(selectedAttributes, selectedMemberDetails, purchaseAttributes)
    );
    const isFormValid = useMemo(() => {
        return validatePerLifeFormSelection(
            step,
            selectedMembers,
            isProceedEnabled,
            purchaseAttributes,
            formValues,
            showError
        );
    }, [step, selectedMembers, isProceedEnabled, purchaseAttributes, formValues]);

    const handleProceed = () => {
        const isEditMode = planIds.some((p) => selectedBenefits.includes(p));
        const { updatedBenefits, updatedUserBenefits } = getPerLifeUpdatedSelections(
            planIds,
            values,
            selectedBenefits,
            userBenefits
        );
        if (hasPurchaseAttributes && step === 1) {
            setStep(2);
        } else {
            setIsVisible(false);
            const recentAttributes = removeAllExtraPurchaseAttributes(
                isEditMode,
                planIds,
                updatedBenefits,
                selectedMembers,
                selectedAttributes
            );
            dispatch(setModularUserBenefits(updatedUserBenefits));
            dispatch(setModularSelectedAlaCarte(updatedBenefits));
            if (hasPurchaseAttributes) {
                const updatedAttributes = getUpdatedAttributesForPerLife(
                    values,
                    formValues,
                    selectedMembers,
                    recentAttributes
                );
                dispatch(setPurchaseAttributes(updatedAttributes));
            }
            toast?.warning(
                isEditMode ? 'You’re good to go — We’ve saved your progress.' : `${plan.name} added successfully`
            );
            setStep(1);
        }
    };

    const handleConfirmDelete = () => {
        const { updatedBenefits, updatedUserBenefits } = getPerLifeUpdatedSelections(
            planIds,
            values,
            selectedBenefits,
            userBenefits,
            true
        );
        const isEditMode = planIds.some((p) => selectedBenefits.includes(p));
        const recentAttributes = removeAllExtraPurchaseAttributes(
            isEditMode,
            planIds,
            updatedBenefits,
            selectedMembers,
            selectedAttributes
        );
        dispatch(setPurchaseAttributes(recentAttributes));
        dispatch(setModularUserBenefits(updatedUserBenefits));
        dispatch(setModularSelectedAlaCarte(updatedBenefits));
        setDeletionModalVisible(false);
        setSelectedItem('');
        toast?.warning(`${plan.name} removed successfully`);
    };

    return (
        <>
            <Modal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                disablePadding={true}
                isModalClosable={false}
                closeOnBlur={false}
                overflow="visible"
            >
                <WithThemeProvider>
                    <StyledClose onClick={handleCloseModal}>
                        <Close />
                    </StyledClose>
                    <StyledBenefitPerLifeSelectionModal data-testid="ala-carte-benefit-per-life-selection-modal">
                        <StyledBenefitPerLifeSelectionModalHeader
                            id="per-life-modal-header"
                            data-testid="ala-carte-benefit-per-life-selection-modal-header-text"
                        >
                            <StyledBenefitPerLifeSelectionModalHeaderText>
                                <Typography variant="large" weight="semiBold" color="tertiary">
                                    Get Started with Your Benefit
                                </Typography>
                            </StyledBenefitPerLifeSelectionModalHeaderText>
                            <StylePerLifeUserLogoHeaders>
                                <Shadow variant="nav">
                                    <WithThemeProvider>
                                        <StyledBenefitPrimaryInfo>
                                            <CardIcon imageUrl={plan.logo} />
                                            <Typography variant="medium" weight="medium" color="primary">
                                                {plan.name}
                                            </Typography>
                                        </StyledBenefitPrimaryInfo>
                                    </WithThemeProvider>
                                </Shadow>
                                {hasPurchaseAttributes && (
                                    <Stepper
                                        steps={['Choose Members', 'Add Details']}
                                        currentStep={step}
                                        variant= 'checked'
                                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                                        onStepperClicked={() => {}}
                                    />
                                )}
                            </StylePerLifeUserLogoHeaders>
                        </StyledBenefitPerLifeSelectionModalHeader>
                        <PurchaseAttributePerLifeModal
                            plan={plan}
                            planIds={planIds}
                            values={values}
                            setValues={setValues}
                            members={members}
                            uncoveredText={uncoveredText}
                            step={step}
                            purchaseAttributes={purchaseAttributes}
                            formValues={formValues}
                            setFormValues={setFormValues}
                            setShowError={setShowError}
                        />
                        <StyledBenefitPerLifeSelectionModalFooter>
                            <StyledBenefitPerLifeSelectionModalFooterLeft>
                                <Typography variant="extraSmall">Total Price for Add-on</Typography>
                                <Typography
                                    variant="title2"
                                    weight="medium"
                                    color={isCostZero ? 'secondary' : 'primary'}
                                >
                                    {isCostZero ? '₹-,---' : formatAmount(totalCost)}
                                    <Typography
                                        variant="extraSmall"
                                        weight="medium"
                                        color={isCostZero ? 'secondary' : 'primary'}
                                    >
                                        /yr
                                    </Typography>
                                </Typography>
                            </StyledBenefitPerLifeSelectionModalFooterLeft>
                            <StyledBenefitPerLifeSelectionModalFooterRight>
                                {hasNotSelectedValues && (
                                    <StyledBenefitPerLifeSelectionModalFooterError>
                                        <div>
                                            <InfoIcon width="20" height="20" strokeColor={theme.colors.error} />
                                        </div>
                                        <Typography variant="small" color="error">
                                            Pick a benefit tier to proceed
                                        </Typography>
                                    </StyledBenefitPerLifeSelectionModalFooterError>
                                )}
                                {hasPurchaseAttributes && step === 2 && (
                                    <LoopButton
                                        onClick={() => {
                                            setStep(1);
                                        }}
                                        variant={step === 2 ? 'secondary' : 'disabled'}
                                        width="240px"
                                    >
                                        {'Back'}
                                    </LoopButton>
                                )}
                                <LoopButton
                                    onClick={handleProceed}
                                    variant={isFormValid ? 'filled' : 'disabled'}
                                    width="240px"
                                >
                                    {hasPurchaseAttributes ? (step === 1 ? 'Next' : 'Confirm') : 'Confirm'}
                                </LoopButton>
                            </StyledBenefitPerLifeSelectionModalFooterRight>
                        </StyledBenefitPerLifeSelectionModalFooter>
                    </StyledBenefitPerLifeSelectionModal>
                </WithThemeProvider>
            </Modal>
            <Modal
                isVisible={!!deletionModalVisible}
                setIsVisible={setDeletionModalVisible}
                isModalClosable={true}
                disablePadding
            >
                <WithThemeProvider>
                    <StyledBenefitPerLifeRemovalModal>
                        <img src={ShruggingGraphic} />
                        <StyledBenefitPerLifeRemovalModalBody>
                            <Typography variant="title2" weight="medium">
                                Remove Add-on?
                            </Typography>
                            <StyledBenefitPerLifeRemovalModalContent>
                                <Typography variant="medium" weight="medium">
                                    All related benefits will no longer be available.
                                </Typography>
                                <Typography variant="small">Are you sure you want to remove this add-on?</Typography>
                            </StyledBenefitPerLifeRemovalModalContent>
                        </StyledBenefitPerLifeRemovalModalBody>
                        <StyledBenefitPerLifeRemovalModalFooter>
                            <LoopButton onClick={() => setDeletionModalVisible(false)} variant="secondary">
                                Cancel
                            </LoopButton>
                            <LoopButton onClick={handleConfirmDelete} variant="filled">
                                Yes, Remove
                            </LoopButton>
                        </StyledBenefitPerLifeRemovalModalFooter>
                    </StyledBenefitPerLifeRemovalModal>
                </WithThemeProvider>
            </Modal>
        </>
    );
};

export default BenefitPerLifeSelectionModal;
